import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { StandingsTable } from './StandingsTable';
import { VotingRulesCard } from './VotingRulesCard';
import type { PoliticsShellProps } from '../../pages/Politics';

/**
 * SF-152: thin wrapper around the pre-existing desktop Politics JSX — structure and every value
 * shown is unchanged from before this migration, apart from a focus-managed <h1> replacing the
 * old <h2>Kingdom Politics</h2>, and VotingRulesCard(variant="plain") replacing the inline intro
 * <div> (byte-identical rendered markup). StandingsTable's own internals are refactored onto
 * SortableTable — see that file's accepted-delta accounting.
 */
export function DesktopPoliticsShell({ standings, myProvinceId, isVoting, handleVote, handleRetract }: PoliticsShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  return (
    <div style={{ padding: 16 }}>
      <h1 ref={h1Ref} tabIndex={-1} style={{ margin: '0 0 12px', color: COLORS.text.primary, fontSize: 18, outline: 'none' }}>
        Politics
      </h1>

      <VotingRulesCard leadRequirement={standings.leadRequirement} variant="plain" />

      {/* Compact status line */}
      <div style={{ marginBottom: 12, padding: '10px 12px', background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13 }}>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: COLORS.text.tertiary }}>
            Monarch:{' '}
            <span style={{ color: standings.monarchProvinceId ? COLORS.gold : COLORS.text.muted, fontWeight: 600 }}>
              {standings.monarchProvinceName ?? 'No Monarch'}
              {standings.monarchProvinceId && <span style={{ marginLeft: 4 }}>♛</span>}
            </span>
          </span>
          <span style={{ color: COLORS.text.tertiary }}>
            Votes cast: <span style={{ color: COLORS.text.secondary }}>{standings.totalVotesCast}</span>
            <span style={{ color: COLORS.text.muted }}> of {standings.totalPossibleVotes}</span>
          </span>
          <span style={{ color: COLORS.text.tertiary }}>
            To crown: <span style={{ color: COLORS.text.secondary }}>
              {standings.majorityThreshold} vote{standings.majorityThreshold === 1 ? '' : 's'}
              {standings.leadRequirement > 0 ? `, +${standings.leadRequirement} lead` : ''}
            </span>
          </span>
        </div>
      </div>

      {/* Networth domination banner */}
      {standings.networthDominationActive && (
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#2a1a00', border: '1px solid #6b3d00', borderRadius: 6, fontSize: 13, color: COLORS.warning }}>
          ⚡ Networth Domination — one province controls &gt;50% of kingdom networth and holds the throne
        </div>
      )}

      <StandingsTable
        standings={standings.standings}
        myVotedForProvinceId={standings.myVotedForProvinceId}
        myProvinceId={myProvinceId}
        onVote={handleVote}
        onRetract={handleRetract}
        isVoting={isVoting}
      />
    </div>
  );
}
